import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import './AgentInfo.css';

const storySections = [
  {
    eyebrow: 'Experience That Makes a Difference',
    title: 'Local Expertise. Broader Perspective.',
    body: 'With years of experience in South Florida real estate and a background in finance and international business, Peter brings a unique combination of local market knowledge and global perspective. He understands the nuances of coastal and luxury markets, works with domestic and international clients, and provides insight that goes beyond the transaction to help clients make confident, well-informed decisions.',
    image: SITE_CONFIG.LANDING_PAGE.CITY_IMAGES.WEST_PALM_BEACH,
    alt: 'South Florida waterfront neighborhood'
  },
  {
    eyebrow: 'A Client-Focused Approach',
    title: 'Your Goals. His Priority.',
    body: 'Peter takes the time to listen, understand his clients’ needs, and provide honest, practical advice. He is known for being responsive, accessible, and committed to protecting his clients’ interests — whether they are buying a first home, selling a longtime property, or building an investment portfolio.',
    image: SITE_CONFIG.LANDING_PAGE.AGENT_EDITORIAL_IMAGE,
    alt: 'Peter Mergenthaler Jr advising a client from his office'
  },
  {
    eyebrow: 'Market Strategy That Adds Value',
    title: 'Insight. Positioning. Results.',
    body: (
      <>
        <p>Peter approaches every property through the lens of the market — analyzing comparable sales, current competition, buyer demand, pricing trends, and the factors that influence value and marketability. For sellers, that insight shapes a tailored strategy for pricing, preparation, positioning, presentation, and marketing designed to attract the right buyers and strengthen the property’s position in the market.</p>
        <p>For buyers and investors, the same market discipline helps identify value, assess opportunities, understand negotiating leverage, and make better-informed decisions.</p>
      </>
    ),
    image: '/about-market-analysis.png',
    alt: 'South Florida real estate market analysis and comparable property report'
  },
  {
    eyebrow: 'Strategic Insight. Disciplined Execution.',
    title: 'From Strategy to a Successful Outcome.',
    body: (
      <>
        <p>A strong strategy only creates value when it is executed well. Peter brings structure, preparation, and attention to detail to every stage of the transaction — from due diligence and financing considerations to negotiations, inspections, insurance, association requirements, and closing.</p>
        <p>His focus is to anticipate potential issues, protect the client’s position, and keep the transaction moving efficiently toward a successful outcome.</p>
      </>
    ),
    image: '/about-closing-handshake.png',
    alt: 'Successful real estate closing with a handshake and house key'
  }
];

const AgentInfo = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const canonical = `${SEO_CONFIG.DOMAIN}/agent`;
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="about-peter-page">
      <Helmet>
        <title>About Peter Mergenthaler Jr | Palm Beach County Real Estate Broker</title>
        <meta name="description" content="Meet Peter Mergenthaler Jr, Broker Owner of Integritos Real Estate and a trusted South Florida real estate advisor with 34 years of experience." />
        <link rel="canonical" href={canonical} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <header className="about-header">
        <Link to="/" className="about-wordmark" aria-label="Integritos Real Estate home" onClick={closeMenu}>
          <img src="/logo.png" alt="" />
          <span><strong>INTEGRITOS</strong><small>REAL ESTATE</small></span>
        </Link>
        <button
          className="about-menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="about-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
          <span className="sr-only">Toggle navigation</span>
        </button>
        <nav id="about-navigation" className={`about-nav${menuOpen ? ' is-open' : ''}`} aria-label="Primary navigation">
          <Link to="/" onClick={closeMenu}>Home</Link>
          <Link className="active" to="/agent" onClick={closeMenu}>About</Link>
          <Link to="/search" onClick={closeMenu}>Buy</Link>
          <Link to="/contact-us" onClick={closeMenu}>Sell</Link>
          <Link to="/search" onClick={closeMenu}>Invest</Link>
          <a href="/#service-areas" onClick={closeMenu}>Areas</a>
          <Link to="/blogs" onClick={closeMenu}>Blog</Link>
          <Link to="/contact-us" onClick={closeMenu}>Contact</Link>
        </nav>
        <Link className="about-header-cta" to="/contact-us">Let’s Talk</Link>
      </header>

      <main>
        <section className="about-hero" style={{ '--about-waterfront': `url(${SITE_CONFIG.LANDING_PAGE.CITY_IMAGES.WEST_PALM_BEACH})` }}>
          <div className="about-hero-shade" />
          <div className="about-hero-inner">
            <div className="about-hero-portrait">
              <img src={SITE_CONFIG.LANDING_PAGE.AGENT_IMAGE} alt="Peter Mergenthaler Jr, Broker Owner" />
              <div className="about-hero-identity">
                <strong>Peter Mergenthaler Jr.</strong>
                <span>Broker Owner</span>
                <small>Strategy. Insight. Results.</small>
              </div>
            </div>
            <div className="about-hero-copy">
              <span className="about-eyebrow light">About Peter</span>
              <h1>More Than Real Estate.<br />A Better Way Forward.</h1>
              <p>Peter Mergenthaler Jr. is the Broker Owner of Integritos Real Estate, where he helps clients buy, sell, and invest in South Florida with knowledge, integrity, and a long-term perspective.</p>
              <p>With a background in finance and international business, Peter brings a unique combination of local expertise and global perspective to every transaction — helping clients make confident, well-informed decisions and achieve their real estate goals.</p>
              <div className="about-hero-callout">Local Expertise. Global Perspective.</div>
            </div>
          </div>
        </section>

        <section className="about-credentials" aria-label="Peter's credentials">
          <div><strong>34</strong><span>Years Experience</span></div>
          <div><strong>GRI</strong><span>Graduate, REALTOR® Institute</span></div>
          <div><strong>5.0</strong><span>Zillow Rating</span></div>
          <div><strong>FL</strong><span>Licensed Broker</span></div>
        </section>

        <section className="about-story" aria-label="Peter's real estate approach">
          {storySections.map((section, index) => (
            <article className="about-story-row" key={section.title}>
              <div className="about-story-image"><img src={section.image} alt={section.alt} loading={index > 0 ? 'lazy' : 'eager'} /></div>
              <div className="about-story-copy">
                <span className="about-eyebrow">{section.eyebrow}</span>
                <h2>{section.title}</h2>
                {typeof section.body === 'string' ? <p>{section.body}</p> : section.body}
              </div>
            </article>
          ))}
        </section>

        <section className="about-reviews">
          <blockquote style={{ '--about-review-image': `url(${SITE_CONFIG.LANDING_PAGE.EDITORIAL_IMAGES.LIVING_ROOM})` }}>
            <span className="about-quote-mark" aria-hidden="true">“</span>
            <p>Knowledgeable, responsive, patient, and genuinely committed to our interests.</p>
            <cite>— Client Review</cite>
          </blockquote>
          <div className="about-reviews-copy">
            <span className="about-eyebrow">Client Experiences</span>
            <h2>What Clients Say.</h2>
            <p>Clients consistently describe Peter as knowledgeable, responsive, patient, and genuinely committed to their interests. Beyond his experience and strategic approach, Peter believes real estate is ultimately about people — listening carefully, being accessible, communicating honestly, and understanding what matters most to each client. That combination of professional expertise and personal attention is at the heart of every relationship he builds.</p>
            <div className="about-review-links">
              <a href={SITE_CONFIG.AGENT.ZILLOW_PROFILE} target="_blank" rel="noopener noreferrer">Read Peter’s Zillow Reviews</a>
              <a href={SITE_CONFIG.AGENT.GOOGLE_REVIEW} target="_blank" rel="noopener noreferrer">View Integritos Real Estate on Google</a>
            </div>
          </div>
        </section>

        <section className="about-areas">
          <div className="about-area-icon" aria-hidden="true"><svg viewBox="0 0 48 48"><path d="M24 43s14-12.2 14-25A14 14 0 1 0 10 18c0 12.8 14 25 14 25Z"/><circle cx="24" cy="18" r="5"/></svg></div>
          <div>
            <span className="about-eyebrow light">Areas Served</span>
            <h2>Serving South Florida</h2>
            <p>Boca Raton <i /> Delray Beach <i /> Highland Beach <i /> Gulf Stream <i /> Boynton Beach<br />Palm Beach <i /> And surrounding areas</p>
          </div>
        </section>

        <section className="about-final-cta" style={{ '--about-cta-image': `url(${SITE_CONFIG.LANDING_PAGE.CITY_IMAGES.FORT_LAUDERDALE})` }}>
          <div>
            <h2>Let’s Talk About Your Next Move</h2>
            <p>Tell me what you want to accomplish. I’ll help you understand your options, define the right strategy, and move forward with confidence.</p>
            <div className="about-final-actions">
              <Link to="/contact-us">Let’s Talk</Link>
              <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>Call {SITE_CONFIG.AGENT.PHONE_DISPLAY}</a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AgentInfo;
